import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@trevor/ui-components';
import { QUERY_NAME_INPUT } from '@/constants/test-ids';
import { useDataPresentationParam, useDatasourceId } from '@/hooks';
import {
  InputGroup,
  ModalFooterControlsContainer,
} from '@/features/modals/components';
import {
  QueryCategoriesSection,
  useCategoriesSelect,
} from '@/views/QueryBuilder/subcomponents/TopControls/ModalUpdateSavedQuery';
import {
  useCreateSavedQueryMutation,
  useGetSavedQuery,
} from '@/features/query-builder/savedQueriesApiSlice';
import { useModalClose } from '@/features/modals/modalSlice';
import LayerHelper from '@/util/LayerHelper';
import { useSelector } from 'react-redux';
import {
  getChartingOptions,
  getQueryBuilderSteps,
  getSelectedLayerId,
} from '@/store-selectors';
import { selectMetricsDimensionConfiguration } from '@/features/queryMetricsDimensionsConfig/metricConfigSlice';
import { useAppDispatch } from '@/store/hooks';
import { INCREMENT_SAVED_QUERIES_COUNT } from '@/actions/constants';
import { dispatchCustomEvent } from '@/util/createCustomEvent';
import { NEW_SAVED_QUERY_SAVED } from '@/constants/custom-event-names';

export const SaveQueryAsNew: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const handleClose = useModalClose();
  const datasourceId = useDatasourceId();
  const dataPresentation = useDataPresentationParam();
  const nameInputRef = React.useRef<HTMLInputElement>(null);
  const steps = useSelector(getQueryBuilderSteps);
  const selectedStepId = useSelector(getSelectedLayerId);
  const chartConfiguration = useSelector(getChartingOptions);
  const metricsDimensionConfiguration = useSelector(
    selectMetricsDimensionConfiguration,
  );
  const [createSavedQuery, { isLoading }] = useCreateSavedQueryMutation();

  const { data: currSavedQueryData } = useGetSavedQuery();

  const currSavedQueryNameName = currSavedQueryData?._viewRef?._name ?? '';
  const [queryName, setQueryName] = React.useState(currSavedQueryNameName);
  const [categories, setCategories] = useCategoriesSelect();

  React.useEffect(() => {
    nameInputRef.current?.select();
  }, []);

  const layerHelper = new LayerHelper(steps, selectedStepId);
  const activeSteps = layerHelper.activeLayers();
  const inactiveSteps = layerHelper.inactiveLayers();

  const handleSave = async () => {
    const result = await createSavedQuery({
      datasourceId,
      query: {
        name: queryName,
        categories,
        chartConfiguration,
        activeSteps,
        inactiveSteps,
        metricsDimensionConfiguration,
      },
    });

    // TODO: add a proper error handling,
    if (!('data' in result)) return;

    const createdSavedQuerySeriesId = result.data.seriesId;
    const dataPresentationPath = dataPresentation ? `/${dataPresentation}` : '';
    const nextUrl = `/datasources/${datasourceId}/saved-queries/${createdSavedQuerySeriesId}${dataPresentationPath}`;

    // TODO: remove once migrated tpo RTK Query.
    dispatch({ type: INCREMENT_SAVED_QUERIES_COUNT });

    // Navigate within the next loop tick when RTK-Query cache is already updated.
    setTimeout(navigate, 0, nextUrl);

    handleClose();
    dispatchCustomEvent(NEW_SAVED_QUERY_SAVED);
  };

  return (
    <>
      <InputGroup>
        <label htmlFor="trevor-new-query-name">Name your query</label>
        <input
          type="text"
          id={QUERY_NAME_INPUT}
          ref={nameInputRef}
          value={queryName}
          onChange={(e) => setQueryName(e.target.value)}
        />
      </InputGroup>
      <QueryCategoriesSection
        categories={categories}
        onUpdate={setCategories}
      />
      <ModalFooterControlsContainer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          id="btnModalSubmit"
          onClick={handleSave}
          disabled={!queryName.trim() || isLoading}
        >
          Submit
        </Button>
      </ModalFooterControlsContainer>
    </>
  );
};
